/**
 * Sync between the local mirror (Dexie) and Supabase.
 *  - push: outbox entries in order; consecutive inserts into one table are batched.
 *  - pull: rows with updated_at > cursor per table (with a safety overlap), on start,
 *          reconnect, tab focus and every minute; plus Supabase Realtime while online.
 *  - conflicts: last write to reach the server wins (server updated_at). A remote change to
 *    a row that still has local unsent edits is not applied locally; the local edit is sent
 *    next and replaces it, and the user is told it was "updated elsewhere".
 */
import type { RealtimeChannel } from '@supabase/supabase-js';
import { toast } from 'sonner';
import {
  db,
  getMeta,
  PULL_ORDER,
  rowKey,
  setMeta,
  type OutboxEntry,
  type SyncTable,
} from '@/db/dexie';
import { supabase } from '@/lib/supabase';
import { onOutboxChange } from '@/data/write';
import { setSyncRunner, useSyncStatus } from './status';

type Row = Record<string, unknown>;
const PAGE = 1000;
const OVERLAP_MS = 30_000;
const PULL_INTERVAL_MS = 60_000;

let stopFns: (() => void)[] = [];
let pushing = false;
let pushQueued = false;
let pullPromise: Promise<void> | null = null;
let backoffMs = 0;
let retryTimer: ReturnType<typeof setTimeout> | undefined;

function onConflictFor(table: SyncTable): string {
  return table === 'task_tags' ? 'task_id,tag_id' : 'id';
}

async function pendingKeySet(): Promise<Set<string>> {
  return new Set((await db().outbox.toArray()).map((e) => `${e.table}:${e.key}`));
}

/** Write server rows into the local mirror unless the row has unsent local edits. */
async function applyRemote(table: SyncTable, rows: Row[], announce: boolean): Promise<void> {
  if (rows.length === 0) return;
  const pending = await pendingKeySet();
  const accept: Row[] = [];
  let skipped = 0;
  for (const r of rows) {
    if (pending.has(`${table}:${rowKey(table, r)}`)) skipped++;
    else accept.push(r);
  }
  if (accept.length) await db().table_(table).bulkPut(accept);
  if (skipped && announce)
    toast('Updated on another device', { description: 'Your newer change here will be kept.' });
}

async function pullTable(table: SyncTable): Promise<number> {
  const client = supabase!;
  const cursorKey = `cursor:${table}`;
  const cursor = (await getMeta<string>(cursorKey)) ?? null;
  const since = cursor ? new Date(Date.parse(cursor) - OVERLAP_MS).toISOString() : null;
  let from = 0;
  let maxSeen = cursor;
  let total = 0;
  for (;;) {
    let q = client
      .from(table)
      .select('*')
      .order('updated_at', { ascending: true })
      .range(from, from + PAGE - 1);
    if (since) q = q.gt('updated_at', since);
    const { data, error } = await q;
    if (error) throw new Error(`${table}: ${error.message}`);
    const rows = (data ?? []) as Row[];
    await applyRemote(table, rows, false);
    total += rows.length;
    for (const r of rows) {
      const u = r.updated_at as string;
      if (!maxSeen || u > maxSeen) maxSeen = u;
    }
    if (rows.length < PAGE) break;
    from += PAGE;
  }
  if (maxSeen && maxSeen !== cursor) await setMeta(cursorKey, maxSeen);
  return total;
}

export async function pullAll(): Promise<void> {
  if (!supabase) return;
  if (pullPromise) return pullPromise;
  pullPromise = (async () => {
    const st = useSyncStatus.getState();
    st.set({ syncing: true });
    try {
      for (const t of PULL_ORDER) await pullTable(t);
      st.set({ lastSyncedAt: new Date().toISOString(), lastError: null, initialDone: true });
    } catch (e) {
      st.set({ lastError: e instanceof Error ? e.message : String(e) });
    } finally {
      useSyncStatus.getState().set({ syncing: false });
      pullPromise = null;
    }
  })();
  return pullPromise;
}

/** Take the next batch: consecutive upserts to one table, or a single patch. */
function nextBatch(entries: OutboxEntry[]): OutboxEntry[] {
  const first = entries[0];
  if (!first) return [];
  if (first.op === 'patch') return [first];
  const batch = [first];
  for (const e of entries.slice(1)) {
    if (e.op !== 'upsert' || e.table !== first.table || batch.length >= 200) break;
    batch.push(e);
  }
  return batch;
}

async function sendBatch(batch: OutboxEntry[]): Promise<Row[]> {
  const client = supabase!;
  const first = batch[0]!;
  if (first.op === 'upsert') {
    const rows = batch.map((e) => e.payload);
    const { data, error } = await client
      .from(first.table)
      .upsert(rows, { onConflict: onConflictFor(first.table) })
      .select('*');
    if (error) throw Object.assign(new Error(error.message), { code: error.code });
    return (data ?? []) as Row[];
  }
  const match =
    first.table === 'task_tags'
      ? { task_id: first.key.split(':')[0], tag_id: first.key.split(':')[1] }
      : first.table === 'profiles'
        ? { id: first.key }
        : { id: first.key };
  const { data, error } = await client
    .from(first.table)
    .update(first.payload)
    .match(match)
    .select('*');
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return (data ?? []) as Row[];
}

export async function pushOutbox(): Promise<void> {
  if (!supabase || !navigator.onLine) return;
  if (pushing) {
    pushQueued = true;
    return;
  }
  pushing = true;
  const st = useSyncStatus.getState();
  st.set({ syncing: true });
  try {
    for (;;) {
      const d = db();
      const entries = await d.outbox.orderBy('seq').limit(400).toArray();
      if (entries.length === 0) break;
      const batch = nextBatch(entries);
      const seqs = batch.map((e) => e.seq!);
      await d.outbox.where('seq').anyOf(seqs).modify({ inflight: 1 });
      try {
        const returned = await sendBatch(batch);
        await d.outbox.bulkDelete(seqs);
        // Server copies (with server-computed fields) replace local ones when nothing newer is pending.
        await applyRemote(batch[0]!.table, returned, false);
        backoffMs = 0;
      } catch (e) {
        const code = (e as { code?: string }).code;
        const message = e instanceof Error ? e.message : String(e);
        await d.outbox
          .where('seq')
          .anyOf(seqs)
          .modify((x) => {
            x.inflight = 0;
            x.attempts += 1;
            x.last_error = message;
          });
        // Permanent errors (bad data, permissions) are dropped after 5 tries so they can't block the queue.
        if (code && /^(22|23|42)/.test(code) && batch.every((b) => b.attempts + 1 >= 5)) {
          await d.outbox.bulkDelete(seqs);
          toast.error('A change could not be saved to the server', { description: message });
          continue;
        }
        st.set({ lastError: message });
        backoffMs = Math.min(backoffMs ? backoffMs * 2 : 2000, 60_000);
        clearTimeout(retryTimer);
        retryTimer = setTimeout(() => void pushOutbox(), backoffMs);
        break;
      }
    }
    if ((await db().outbox.count()) === 0)
      st.set({ lastError: null, lastSyncedAt: new Date().toISOString() });
  } finally {
    pushing = false;
    useSyncStatus.getState().set({ syncing: false });
    if (pushQueued) {
      pushQueued = false;
      void pushOutbox();
    }
  }
}

function subscribeRealtime(userId: string): RealtimeChannel | null {
  if (!supabase) return null;
  const channel = supabase.channel(`karya-${userId}`);
  for (const table of PULL_ORDER) {
    channel.on('postgres_changes', { event: '*', schema: 'public', table }, (payload) => {
      if (payload.eventType === 'DELETE') {
        const old = payload.old as Row;
        const key = table === 'task_tags' ? [old.task_id, old.tag_id] : old.id;
        if (key)
          void db()
            .table_(table)
            .delete(key as never);
        return;
      }
      void applyRemote(table, [payload.new as Row], true);
    });
  }
  channel.subscribe();
  return channel;
}

/** Start syncing for a signed-in user. Returns once the first pull finished (or failed). */
export async function startSync(userId: string): Promise<void> {
  stopSync();
  setSyncRunner(async () => {
    await pushOutbox();
    await pullAll();
  });
  // Rows queued before a crash were never confirmed: retry them.
  await db().outbox.where('inflight').equals(1).modify({ inflight: 0 });
  let timer: ReturnType<typeof setTimeout> | undefined;
  const schedulePush = () => {
    clearTimeout(timer);
    timer = setTimeout(() => void pushOutbox(), 300);
  };
  const onOnline = () => {
    void pushOutbox().then(pullAll);
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') void pushOutbox().then(pullAll);
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  const interval = setInterval(() => {
    if (document.visibilityState === 'visible' && navigator.onLine) void pullAll();
  }, PULL_INTERVAL_MS);
  const offOutbox = onOutboxChange(schedulePush);
  const channel = subscribeRealtime(userId);
  stopFns = [
    () => window.removeEventListener('online', onOnline),
    () => document.removeEventListener('visibilitychange', onVisible),
    () => clearInterval(interval),
    () => clearTimeout(timer),
    offOutbox,
    () => {
      if (channel) void supabase?.removeChannel(channel);
    },
  ];
  await pushOutbox();
  await pullAll();
}

export function stopSync(): void {
  stopFns.forEach((f) => f());
  stopFns = [];
  setSyncRunner(null);
}
